/*
 * 0.89 (Entwicklerbriefing „Kontext und Projektübergang“, Etappe A):
 * K1 Board-Kontext („Worum es geht“), K2 beschreibende Beziehungen zwischen Karten und Relevanz von Unterlagen,
 * K4 Handlungshinweise. Nur Typen, Texte und reine Hilfen – Rechte und Auswertung liegen auf dem Server.
 */

/* ---------- K1: Board-Kontext ---------- */

export const CONTEXT_MAX_REFS = 5;
export const CONTEXT_OUTCOME_MAX = 300;
export const CONTEXT_PURPOSE_MAX = 80;

/** Ein wichtiger Bezug: vorhandene Karte, Bibliotheksdatei oder sichere externe Adresse */
export type ContextRefInput =
  | { kind: 'card'; cardId: string; purpose: string | null }
  | { kind: 'file'; libraryFileId: string; purpose: string | null }
  | { kind: 'link'; url: string; title: string; purpose: string | null };

/**
 * Aufgelöster Bezug (Titel, Fassung usw. werden beim Lesen aus dem Original geholt – keine zweite Pflege).
 * `unavailable` nur für Personen, die den Kontext bearbeiten dürfen – ohne Namen oder andere Metadaten.
 */
export type ContextRef =
  | { id: string; kind: 'card'; cardId: string; title: string; archived: boolean; done: boolean; columnTitle: string | null; purpose: string | null }
  | { id: string; kind: 'file'; libraryFileId: string; file: import('./library.js').LibraryFile; team: boolean; purpose: string | null }
  | { id: string; kind: 'link'; url: string; title: string; purpose: string | null }
  | { id: string; kind: 'unavailable'; purpose: null };

export interface BoardContext {
  /** Boardbeschreibung – bleibt der maßgebliche Text (kein zweiter Langtext) */
  description: string | null;
  /** knappes erwartetes Ergebnis (optional) */
  outcome: string | null;
  refs: ContextRef[];
  /** Versionsstand für das Speichern ohne stilles Überschreiben */
  version: number;
  canEdit: boolean;
}

/** Hat das Board überhaupt Kontextangaben? Ohne Angaben: kein leerer Kasten. */
export const hasContext = (c: Pick<BoardContext, 'description' | 'outcome' | 'refs'> | null | undefined): boolean =>
  !!c && (!!c.description?.trim() || !!c.outcome?.trim() || c.refs.some((r) => r.kind !== 'unavailable'));

/* ---------- K2: Beziehungen ---------- */

export const RELATION_KINDS = ['related', 'basis', 'delivers'] as const;
export type RelationKind = (typeof RELATION_KINDS)[number];

/** Beschreibende Beziehung zwischen zwei Karten desselben Boards – keine Sperre, kein Statuswechsel */
export interface CardRelation {
  id: string;
  kind: RelationKind;
  /** bei „related“ ungerichtet (gespeichert mit fromId < toId) */
  fromId: string;
  toId: string;
}

/** Sicht von einer Karte aus: Satzanfang je Richtung */
export type RelationView = 'related' | 'basisFor' | 'basedOn' | 'deliversTo' | 'receivesFrom';
export const RELATION_VIEW_TEXT: Record<RelationView, string> = {
  related: 'Gehört zusammen mit',
  basisFor: 'Ist Grundlage für',
  basedOn: 'Baut auf',
  deliversTo: 'Liefert Ergebnis für',
  receivesFrom: 'Erhält Ergebnis von',
};
export const RELATION_VIEW_HELP: Record<RelationView, string> = {
  related: 'Inhaltlicher Zusammenhang, keine Reihenfolge.',
  basisFor: 'Diese Karte erklärt, worauf die andere aufbaut. Sperrt nichts.',
  basedOn: 'Die andere Karte ist Grundlage für diese. Sperrt nichts.',
  deliversTo: 'Das Ergebnis dieser Karte geht in die andere ein. Erledigen verschiebt dort nichts.',
  receivesFrom: 'Die andere Karte liefert ein Ergebnis für diese. Erledigen verschiebt hier nichts.',
};
export const RELATION_VIEWS: RelationView[] = ['related', 'basisFor', 'basedOn', 'deliversTo', 'receivesFrom'];

/** Wie sieht `cardId` die Beziehung? null = betrifft die Karte nicht */
export function relationView(r: CardRelation, cardId: string): { view: RelationView; otherId: string } | null {
  if (r.fromId !== cardId && r.toId !== cardId) return null;
  const out = r.fromId === cardId;
  const otherId = out ? r.toId : r.fromId;
  if (r.kind === 'related') return { view: 'related', otherId };
  if (r.kind === 'basis') return { view: out ? 'basisFor' : 'basedOn', otherId };
  return { view: out ? 'deliversTo' : 'receivesFrom', otherId };
}

/** Aus einer Sicht (Karte → andere Karte) den gespeicherten Datensatz bilden – ungerichtet normalisiert */
export function relationFromView(view: RelationView, cardId: string, otherId: string): Omit<CardRelation, 'id'> {
  switch (view) {
    case 'related':
      return cardId < otherId ? { kind: 'related', fromId: cardId, toId: otherId } : { kind: 'related', fromId: otherId, toId: cardId };
    case 'basisFor':
      return { kind: 'basis', fromId: cardId, toId: otherId };
    case 'basedOn':
      return { kind: 'basis', fromId: otherId, toId: cardId };
    case 'deliversTo':
      return { kind: 'delivers', fromId: cardId, toId: otherId };
    case 'receivesFrom':
      return { kind: 'delivers', fromId: otherId, toId: cardId };
  }
}

/** Schlüssel für die Duplikatprüfung: höchstens eine beschreibende Beziehung je Kartenpaar */
export const relationPairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

/* ---------- K2: Relevanz von Unterlagen ---------- */

export const ATTACHMENT_RELEVANCE = ['briefing', 'reference', 'result'] as const;
export type AttachmentRelevance = (typeof ATTACHMENT_RELEVANCE)[number];
export const RELEVANCE_TEXT: Record<AttachmentRelevance, string> = { briefing: 'Briefing', reference: 'Referenz', result: 'Ergebnis' };

/* ---------- K4: Handlungshinweise ---------- */

/** Wohin ein Hinweis führt (Bearbeitungsort in den Kartendetails) */
export type HintTarget =
  | { kind: 'field'; fieldId: string }
  | { kind: 'due' }
  | { kind: 'start' }
  | { kind: 'checklist' }
  | { kind: 'files' }
  | { kind: 'assignees' }
  | { kind: 'labels' }
  | { kind: 'priority' }
  | { kind: 'estimate' }
  | { kind: 'deps' }
  | { kind: 'block' }
  | { kind: 'done' };

export interface HintItem {
  /** Voraussetzung in Worten („„Freigabe“ ist freigegeben“) */
  text: string;
  target: HintTarget | null;
  /** Zusatz, z. B. warum eine Freigabe verfallen ist */
  note?: string | null;
}

/**
 * Ein aktueller Grund, warum ein Schritt (noch) nicht geht – aus derselben Auswertung wie der tatsächliche
 * Wechsel (Spaltenregeln mit der betrachtenden Person). Keine Schreibwirkung, keine Zusage für später.
 */
export interface CardHint {
  kind: 'exitGate' | 'entryGate';
  /** Satz für den Arbeitsstand */
  text: string;
  columnId: string | null;
  columnTitle: string | null;
  /** eigener Hinweis der Spaltenregel (falls gesetzt) */
  message: string | null;
  items: HintItem[];
  /** Besitzer*in darf die Regel mit Begründung übergehen */
  canOverride: boolean;
}

/* ---------- 0.90 (K3): Beteiligte ohne Konto ---------- */

export const PARTICIPANT_NAME_MAX = 80;
export const PARTICIPANT_ORG_MAX = 80;
export const PARTICIPATION_ROLE_MAX = 40;
export const PARTICIPATION_CONTRIBUTION_MAX = 160;
/** Höchstens so viele Beteiligte je Board und Beteiligungen je Karte */
export const PARTICIPANTS_MAX = 200;
export const PARTICIPATIONS_PER_CARD_MAX = 20;
/** Vorschläge für die Rolle – frei änderbar, keine Rechte daraus */
export const PARTICIPATION_ROLE_SUGGESTIONS = ['liefert', 'prüft', 'Ansprechperson', 'informiert'] as const;

/** Klarer Satz zur Sichtbarkeit – bei Aktivierung und Erfassung angezeigt */
export const PARTICIPANTS_VISIBILITY =
  'Sichtbar für alle, die dieses Board lesen dürfen – auch für Gäste mit Zugang zu diesem Board. Beteiligte erhalten keinen Zugang, keine Nachricht und keine Einladung.';

/** Person oder Stelle ohne Kaba-Konto, nur in diesem Board (Name/Bezeichnung und Organisation – keine Kontaktdaten) */
export interface BoardParticipant {
  id: string;
  name: string;
  organization: string | null;
  version: number;
}

/** Beteiligung an einer Karte: Rolle und kurzer Beitrag; ohne Person = offener Rollenplatz (z. B. aus einer Vorlage) */
export interface CardParticipation {
  id: string;
  cardId: string;
  participantId: string | null;
  role: string;
  contribution: string | null;
  version: number;
}

/** „Name (Organisation)“ */
export function participantLabel(p: Pick<BoardParticipant, 'name' | 'organization'>): string {
  return p.organization ? `${p.name} (${p.organization})` : p.name;
}
